import type { IManufacturingFacilityInformationFormData } from "@/context/data.types";
import { normalizeNewOrExistingSiteValue } from "@/utils/vendor/siteApiFieldUtils";

const NEW_SITE_MODE = "new";

/** Keep these as "" in the save payload (don't strip with removeEmptyValues) so an unselected mode clears the stored value. */
export const MANUFACTURING_FACILITY_PRESERVE_EMPTY_FIELDS = ["newOrExistingSite", "isThisNewOrExistingSite"];

export function hasPersistedManufacturingFacilityId(f: Record<string, unknown>): boolean {
  const id = f.id;
  if (id == null) return false;
  const s = String(id).trim();
  if (s === "") return false;
  const n = Number(s);
  return Number.isFinite(n) && n > 0;
}

/** Non-empty user/site data for a "new" facility row (raw form, before map). */
function hasSubstantiveNewSiteFields(f: Record<string, unknown>): boolean {
  const textFields: (keyof IManufacturingFacilityInformationFormData | "siteType" | "siteCode")[] = [
    "name",
    "contact",
    "emailId",
    "phone",
    "manufacturerAddress",
    "manufacturerCity",
    "manufacturerState",
    "manufacturerZip",
    "manufacturerCountry",
    "siteType",
    "siteCode",
  ];
  for (const k of textFields) {
    const v = f[k as string];
    if (v == null) continue;
    if (typeof v === "object" && v !== null && "id" in (v as object)) {
      const inner = (v as { id?: unknown }).id;
      if (inner != null && String(inner).trim() !== "") return true;
      continue;
    }
    if (String(v).trim() !== "") return true;
  }
  return false;
}

/**
 * Raw form row: omit from save when:
 * - "Existing" is chosen but no catalogue site is selected, or
 * - "New" is chosen (existing-vendor flow), row was never persisted, and no site name / substantive fields.
 */
export function shouldOmitManufacturingFacilityFromSavePayload(
  facility: IManufacturingFacilityInformationFormData | Record<string, unknown>
): boolean {
  const f = facility as Record<string, unknown>;
  const mode = String(f.newOrExistingSite ?? "").toLowerCase();

  const isExisting = mode === "existing";
  if (isExisting) {
    const raw = f.existingSiteId;
    const hasPick = raw != null && String(raw).trim() !== "";
    // Only omit unpersisted stubs: saved rows may lack existingSiteId in state but still have MF id + data.
    return !hasPick && !hasPersistedManufacturingFacilityId(f);
  }

  const isExplicitNew = mode === NEW_SITE_MODE;
  if (!isExplicitNew) return false;

  if (hasPersistedManufacturingFacilityId(f)) return false;
  if (hasSubstantiveNewSiteFields(f)) return false;
  return true;
}

/**
 * After map + removeEmptyValues: drop stub rows (e.g. only `newOrExistingSite: "existing"`).
 */
export function shouldDropIncompleteExistingManufacturingFacilityPayload(
  facility: Record<string, unknown> | null | undefined
): boolean {
  if (facility == null || typeof facility !== "object") return false;
  const isExisting =
    String(facility.newOrExistingSite ?? "").toLowerCase() === "existing";
  if (!isExisting) return false;
  if (hasPersistedManufacturingFacilityId(facility)) return false;
  const id = facility.id;
  const hasId = id != null && String(id).trim() !== "";
  return !hasId;
}

/**
 * After map + removeEmptyValues: drop stub "new" rows (e.g. only `newOrExistingSite: "new"`), never persisted.
 */
export function shouldDropIncompleteNewManufacturingFacilityPayload(
  facility: Record<string, unknown> | null | undefined
): boolean {
  if (facility == null || typeof facility !== "object") return false;
  const mode = String(facility.newOrExistingSite ?? "").toLowerCase();
  if (mode !== NEW_SITE_MODE) return false;
  if (hasPersistedManufacturingFacilityId(facility)) return false;
  return !hasSubstantiveNewSiteFields(facility);
}

/**
 * Save API expects the selected existing-site catalogue id as `id`, not `existingSiteId`.
 * Form state keeps `existingSiteId` for UI (dropdown, cross-tab filtering).
 */
export function mapManufacturingFacilityForApiPayload(
  facility: IManufacturingFacilityInformationFormData | Record<string, unknown>
): Record<string, unknown> {
  const f = facility as Record<string, unknown>;
  const isExisting =
    String(f.newOrExistingSite ?? "").toLowerCase() === "existing";
  const rawSiteId = f.existingSiteId;
  const { existingSiteId: _omit, ...rest } = f;
  const out: Record<string, unknown> = { ...rest };
  if (isExisting && rawSiteId != null && String(rawSiteId).trim() !== "") {
    out.id = String(rawSiteId).trim();
  }
  // No catalogue pick: strip name/id only for unpersisted stubs (saved rows keep MF id + name in payload).
  if (isExisting && (rawSiteId == null || String(rawSiteId).trim() === "")) {
    if (!hasPersistedManufacturingFacilityId(f)) {
      delete out.name;
      delete out.id;
    }
  }
  const mode = normalizeNewOrExistingSiteValue(f.newOrExistingSite);
  if (mode === "new" || mode === "existing") {
    out.newOrExistingSite = mode;
    out.isThisNewOrExistingSite = mode;
  } else {
    // Nothing selected: send the fields empty (not removed) so a stale value (e.g. "existing" from the API) isn't kept
    out.newOrExistingSite = "";
    out.isThisNewOrExistingSite = "";
  }
  return out;
}
